import {RefObject, useEffect, useMemo, useRef, useState} from "react";
import {ProductDemoViewModel} from "../../api/models";
import {ProductsApi} from "../../api/ProductsApi";
import {fromBase64} from "../../components/demoAnalysis";

// The demo player's pictures of the sound, from the demo's analysis (components/demoAnalysis.ts, worked out when it
// was uploaded): its waveform, under the seek slider, and its spectrum, moving with the music. Demos without one (or
// whose analysis can't be loaded) have neither, and the player shows its plain slider.

export interface DemoPicture {
    peaks: Uint8Array;
    spectrum: Uint8Array;
    frameRate: number;
    bands: number;
}

// Loaded once per demo, for as long as the page is open.
const loaded = new Map<string, Promise<DemoPicture | undefined>>();

export function useDemoPicture(productId: string, demo: ProductDemoViewModel | undefined): DemoPicture | undefined {
    const [picture, setPicture] = useState<{ id: string, value?: DemoPicture }>();
    useEffect(() => {
        if (!demo?.hasAnalysis) {
            return;
        }
        let current = true;
        if (!loaded.has(demo.id)) {
            loaded.set(demo.id, ProductsApi.getDemoAnalysis(productId, demo.id).then(
                x => ({ peaks: fromBase64(x.peaks), spectrum: fromBase64(x.spectrum), frameRate: x.frameRate, bands: x.bands }),
                () => {
                    loaded.delete(demo.id); // tried again next time
                    return undefined;
                }));
        }
        void loaded.get(demo.id)!.then(value => current && setPicture({ id: demo.id, value }));
        return () => { current = false; };
    }, [productId, demo?.id, demo?.hasAnalysis]);
    return picture && picture.id === demo?.id ? picture.value : undefined;
}

const WAVE_BARS = 128;

// The waveform, twice: dim, and in the accent clipped to what's been played (played: 0-1).
export function Waveform({peaks, played}: { peaks: Uint8Array, played: number }) {
    const heights = useMemo(() => Array.from({ length: WAVE_BARS }, (_, i) => {
        const from = Math.floor(i * peaks.length / WAVE_BARS), to = Math.max(from + 1, Math.floor((i + 1) * peaks.length / WAVE_BARS));
        let max = 0;
        for (let k = from; k < to; k++) {
            max = Math.max(max, peaks[k]);
        }
        return `${Math.max(4, Math.round(max / 255 * 100))}%`;
    }), [peaks]);
    const bars = heights.map((height, i) => <span key={i} style={{ height }}></span>);
    return (
        <div className="demo-player-wave" aria-hidden="true">
            <div className="demo-player-wave-bars">{bars}</div>
            <div className="demo-player-wave-bars demo-player-wave-played" style={{ clipPath: `inset(0 ${(1 - Math.min(1, Math.max(0, played))) * 100}% 0 0)` }}>{bars}</div>
        </div>
    );
}

const motionAllowed = () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// The spectrum's bars, following the audio's current time while it plays and settling when it stops. Drawn straight
// on the bars (transform: scaleY), once a frame, without re-rendering. Not shown to visitors who ask for less motion.
export function Spectrum({picture, audio, playing}: { picture: DemoPicture, audio: RefObject<HTMLAudioElement | null>, playing: boolean }) {
    const bars = useRef<(HTMLSpanElement | null)[]>([]);
    const levels = useRef<number[]>([]);
    const [allowed] = useState(motionAllowed);

    useEffect(() => {
        if (!allowed || typeof requestAnimationFrame === 'undefined') {
            return;
        }
        const {spectrum, frameRate, bands} = picture;
        const frames = spectrum.length / bands;
        let handle = 0;
        const draw = () => {
            const position = (audio.current?.currentTime ?? 0) * frameRate;
            const frame = Math.min(frames - 1, Math.floor(position)), next = Math.min(frames - 1, frame + 1), mix = position - frame;
            let moving = false;
            for (let b = 0; b < bands; b++) {
                // Playing: the analysed level, between frames; stopped: down to rest.
                const target = playing ? ((1 - mix) * spectrum[frame * bands + b] + mix * spectrum[next * bands + b]) / 255 : 0;
                const previous = levels.current[b] ?? 0;
                // Quick to rise, slower to fall, as level meters do.
                const level = target > previous ? previous + (target - previous) * 0.6 : previous + (target - previous) * 0.15;
                levels.current[b] = level;
                moving ||= Math.abs(level - target) > 0.002;
                const bar = bars.current[b];
                if (bar) {
                    bar.style.transform = `scaleY(${Math.max(0.04, level)})`;
                }
            }
            if (playing || moving) {
                handle = requestAnimationFrame(draw);
            }
        };
        handle = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(handle);
    }, [picture, audio, playing, allowed]);

    if (!allowed) {
        return null;
    }
    return (
        <div className="demo-player-spectrum" aria-hidden="true">
            {Array.from({ length: picture.bands }, (_, b) => <span key={b} ref={x => { bars.current[b] = x; }}></span>)}
        </div>
    );
}
